import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, map, Observable, Subject, takeUntil } from 'rxjs';
import { EmailAccountSettingsComponent } from './email-account-settings';
import { CampaignEmailCredential } from 'src/app/shared/models/campaign/campaign-account';
import { EmailCredentialVerificationService } from '../../shared/services/email-credential-verification.service';
import { ValidationCheckStatus } from '../../shared/types';

@Component({
  selector: 'app-email-account-editor-modal',
  templateUrl: './email-account-editor-modal.component.html',
  styleUrls: ['./email-account-editor-modal.component.scss'],
  providers: [EmailCredentialVerificationService],
  standalone: false,
})
export class EmailAcountEditorModalComponent implements OnInit, AfterViewInit, OnDestroy {
  constructor(
    public dialogRef: MatDialogRef<EmailAcountEditorModalComponent>,
    private credentialVerificationService: EmailCredentialVerificationService,
    @Inject(MAT_DIALOG_DATA)
    public settings?: CampaignEmailCredential,
  ) {}

  @ViewChild('accountSettings') accountSettings: EmailAccountSettingsComponent;

  isNewAccount: boolean;
  $unsubscribe = new Subject<void>();
  $saveIsDisabled: Observable<boolean>;

  private formIsValidSubject = new BehaviorSubject<boolean>(false);

  ngOnInit(): void {
    this.isNewAccount = !this.settings;

    this.credentialVerificationService
      .runValidationTask()
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe();

    this.handleSaveButtonDisableState();
  }

  ngAfterViewInit(): void {
    this.accountSettings.setSettings(this.settings);

    this.credentialVerificationService.setSettingsAcquiringCallback(() => {
      return this.accountSettings.getSettings();
    });

    this.disableFormWhileCheckingCredentials();
  }

  closeModal(): void {
    this.dialogRef.close(null);
  }

  save(): void {
    this.dialogRef.close(this.accountSettings.getSettings());
  }

  validStateChange(isValid: boolean): void {
    this.formIsValidSubject.next(isValid);
  }

  private handleSaveButtonDisableState(): void {
    this.$saveIsDisabled = combineLatest([
      this.formIsValidSubject,
      this.credentialVerificationService.getCredentialVerificationStatus$(),
    ]).pipe(
      takeUntil(this.$unsubscribe),
      map(([forIsValid, verificationStatus]: [boolean, ValidationCheckStatus]) => {
        return !forIsValid || verificationStatus !== 'ok';
      }),
    );
  }

  private disableFormWhileCheckingCredentials(): void {
    this.credentialVerificationService
      .getCredentialVerificationStatus$()
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((status: ValidationCheckStatus) => {
        if (status === 'loading') {
          this.accountSettings.disableForm();
        } else {
          this.accountSettings.enableForm();
        }
      });
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }
}
